import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { gsap } from 'gsap';

import GaneshaCover from '../../../assets/img/website-images/ganesha-visarjana-2026-cover.webp';

/* ─── News & Events Data ──────────────────────────────────────── */
const newsEvents = [
    {
        id: 1,
        date: '18 September, 2026',
        title: 'Ganesha Visarjana 2026',
        image: GaneshaCover,
        description: 'A colourful farewell to Lord Ganesha at the Akash campus, celebrated with devotion and community spirit across the Akash Group of Institutions.',
        time: '1 min read',
        slug: '/news-and-events/ganesha-visarjana-2026',
    },
];

/* ─── Variants ─────────────────────────────────────────────────── */
const gridContainerVariants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.18, delayChildren: 0.1 } },
};

const cardVariants = {
    hidden: { opacity: 0, y: 60, scale: 0.95 },
    visible: {
        opacity: 1, y: 0, scale: 1,
        transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
    },
};

/* ─── Event Card ───────────────────────────────────────────────── */
function EventCard({ event }) {
    const imgRef = useRef(null);
    const overlayRef = useRef(null);

    const handleMouseEnter = () => {
        gsap.to(imgRef.current, { scale: 1.08, duration: 0.5, ease: 'power2.out' });
        gsap.to(overlayRef.current, { opacity: 1, duration: 0.35 });
    };
    const handleMouseLeave = () => {
        gsap.to(imgRef.current, { scale: 1, duration: 0.5, ease: 'power2.out' });
        gsap.to(overlayRef.current, { opacity: 0, duration: 0.35 });
    };

    return (
        <motion.div
            className="col-lg-4 col-md-6"
            variants={cardVariants}
            style={{ marginBottom: '30px' }}
        >
            <div
                className="event-item mb-30"
                style={{ overflow: 'hidden', borderRadius: '10px' }}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                <div className="thumb" style={{ overflow: 'hidden', position: 'relative' }}>
                    <Link to={event.slug}>
                        <img
                            ref={imgRef}
                            src={event.image}
                            alt={event.title}
                            style={{ display: 'block', width: '100%', transformOrigin: 'center' }}
                        />
                    </Link>
                    <div
                        ref={overlayRef}
                        style={{
                            position: 'absolute', inset: 0,
                            background: 'rgba(30, 90, 168, 0.18)',
                            opacity: 0, pointerEvents: 'none',
                        }}
                    />
                </div>

                <div className="event-content">
                    <motion.div
                        className="date"
                        initial={{ opacity: 0, x: -15 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, amount: 0.4 }}
                        transition={{ duration: 0.45, delay: 0.08 }}
                    >
                        {event.date}
                    </motion.div>

                    <motion.h3
                        initial={{ opacity: 0, y: 14 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.4 }}
                        transition={{ duration: 0.5, delay: 0.16 }}
                    >
                        <Link to={event.slug}>{event.title}</Link>
                    </motion.h3>

                    <motion.p
                        initial={{ opacity: 0, y: 14 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.4 }}
                        transition={{ duration: 0.5, delay: 0.24 }}
                    >
                        {event.description}
                    </motion.p>

                    <motion.div
                        className="time"
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true, amount: 0.4 }}
                        transition={{ duration: 0.4, delay: 0.32 }}
                    >
                        {event.time}{' '}
                        <i className="fal fa-long-arrow-right" />{' '}
                        <strong>Akash Institute of Pharmacy</strong>
                    </motion.div>
                </div>
            </div>
        </motion.div>
    );
}

/* ─── Main Component ───────────────────────────────────────────── */
function NewsEvents() {
    const { ref: gridRef, inView: gridInView } = useInView({ threshold: 0.2, triggerOnce: true });

    return (
        <section className="shop-area pt-120 pb-60 p-relative ss2">
            <div className="container">
                <motion.div
                    ref={gridRef}
                    className="row"
                    variants={gridContainerVariants}
                    initial="hidden"
                    animate={gridInView ? 'visible' : 'hidden'}
                >
                    {newsEvents.map((event) => (
                        <EventCard key={event.id} event={event} />
                    ))}
                </motion.div>
            </div>
        </section>
    );
}

export default NewsEvents;
